import React, { useState, useEffect, useCallback } from 'react';

// ── Snackbar context ─────────────────────────────────────────
import { createContext, useContext, useRef } from 'react';

const SnackbarContext = createContext(null);

const ICONS = {
  success: '✓',
  error:   '✕',
  info:    'ℹ',
  warning: '⚠',
};

export const SnackbarProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const show = useCallback((message, type = 'success', duration = 3500, onClick = null) => {
    const id = ++idRef.current;
    setToasts(prev => [...prev, { id, message, type, onClick, hiding: false }]);

    // Start hide animation before removal
    setTimeout(() => {
      setToasts(prev => prev.map(t => t.id === id ? { ...t, hiding: true } : t));
    }, duration - 300);

    // Remove
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, duration);

    return id;
  }, []);

  const dismiss = useCallback((id) => {
    setToasts(prev => prev.map(t => t.id === id ? { ...t, hiding: true } : t));
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 280);
  }, []);

  return (
    <SnackbarContext.Provider value={{ show }}>
      {children}
      <div className="snackbar-container">
        {toasts.map(toast => (
          <div
            key={toast.id}
            className={`snackbar snackbar-${toast.type}${toast.hiding ? ' hide' : ''}${toast.onClick ? ' snackbar-clickable' : ''}`}
            onClick={toast.onClick ? () => { toast.onClick(); dismiss(toast.id); } : undefined}
            role={toast.onClick ? 'button' : undefined}
            tabIndex={toast.onClick ? 0 : undefined}
          >
            <span className="snackbar-icon">{ICONS[toast.type] || '●'}</span>
            <span className="snackbar-msg">{toast.message}</span>
            <button
              className="snackbar-close"
              onClick={(e) => { e.stopPropagation(); dismiss(toast.id); }}
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = () => {
  const ctx = useContext(SnackbarContext);
  if (!ctx) throw new Error('useSnackbar must be used inside SnackbarProvider');
  return ctx.show;
};